// An invite link (decision 065): one door into an airframe, and the next
// step said: ShariFly on the PC that runs X-Plane, where the aircraft is
// accepted.

import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";

import { api, queries, unwrap } from "@/api";
import { ErrorNote } from "@/components/ErrorNote";
import { External } from "@/components/External";
import { SignIn } from "@/components/SignIn";
import { useHost } from "@/host/host";
import { useReleaseLink } from "@/host/links";

export const Route = createFileRoute("/invites/$code")({
  component: AcceptInvite,
});

function AcceptInvite() {
  const { code } = Route.useParams();
  const host = useHost();
  const me = useQuery(queries.me());
  const accept = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/v1/invites/{target}", { params: { path: { target: `${code}:accept` } } })),
  });
  const joined = useQuery({ ...queries.airframe(accept.data?.airframe_id ?? ""), enabled: accept.isSuccess });
  const download = useReleaseLink("companion.msi");
  if (me.isPending) return null;
  return (
    <div className="mx-auto max-w-lg space-y-4 pt-10">
      {!me.data ? (
        <>
          <h1 className="text-2xl font-semibold tracking-tight">You are invited to fly a shared aircraft</h1>
          <p className="muted">An aircraft shared on ShariFly: one that remembers every flight, whoever flew it last. Sign in with Discord to join; you come back to this page.</p>
          <SignIn next={`/invites/${code}`} testId="login-invite" />
        </>
      ) : accept.isSuccess ? (
        <div className="space-y-3" data-testid="invite-accepted">
          <h1 className="text-2xl font-semibold tracking-tight text-ok">
            You are a <b>{accept.data.role}</b> of {joined.data?.identity.reg ?? "the airframe"}
          </h1>
          {host ? (
            <>
              <p className="muted">Next: book the aircraft from your Fleet, with X-Plane closed, then start X-Plane and fly.</p>
              <Link to="/" className="btn btn-primary">
                Open the Fleet
              </Link>
            </>
          ) : (
            <>
              <p className="muted">Next: ShariFly on the PC that runs X-Plane moves the aircraft in and out of your simulator. Sign in there with the same Discord account, then press Book the aircraft.</p>
              <div className="flex flex-wrap gap-2">
                <External href={download} className="btn btn-primary">
                  Download ShariFly for Windows
                </External>
                <a className="btn" href="sharedairframe://open">
                  Already installed? Open ShariFly
                </a>
              </div>
              <p className="muted text-xs">Windows warns once because the installer is not signed yet: More info, Run anyway. The plugin for X-Plane is installed from inside ShariFly.</p>
            </>
          )}
          <p>
            <Link to="/airframes/$id" params={{ id: accept.data.airframe_id }} className="underline">
              Open the airframe
            </Link>
          </p>
        </div>
      ) : (
        <>
          <h1 className="text-2xl font-semibold tracking-tight">You are invited to fly a shared aircraft</h1>
          <p className="muted">Joining makes you a pilot of the airframe the link carries (or a captain, if the owner chose so).</p>
          <button type="button" className="btn btn-primary" onClick={() => accept.mutate()} disabled={accept.isPending} data-testid="accept-invite">
            Join
          </button>
          <ErrorNote error={accept.error} />
        </>
      )}
    </div>
  );
}
